import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Share, Plus, Smartphone, Monitor, Bell, Rocket, Check, ChevronRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";

/**
 * InstallGuide — pedagogical landing page explaining how to install the
 * Helencia PWA on iPhone and Android. Linked from the PWA install banner,
 * the footer and the auth pages.
 *
 * Design notes:
 * - Two-column on desktop (iOS card + Android card), stacked on mobile.
 * - Step-by-step with illustrated chips (icons from lucide-react — no
 *   external screenshots required so the guide stays crisp on any screen).
 * - Benefits bullets at the top so users understand why they'd install.
 */
export default function InstallGuide() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ios"); // mobile picker — defaults to iOS

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="install-guide">
      <SEO
        title="Installer Helencia sur votre téléphone — iPhone & Android"
        description="Guide pas-à-pas pour installer l'application Helencia sur iPhone et Android. Notifications, accès rapide, plein écran — comme une vraie app, sans passer par les stores."
      />
      <Navbar />

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-14 pt-10 sm:pt-16 pb-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#8B8680] hover:text-[#1A1A1A] mb-8">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="caption-label text-[#A26F3E]">Application mobile · PWA</p>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#1A1A1A] mt-3 leading-[1.05]">
            Installez Helencia <span className="italic text-[#A26F3E]">sur votre téléphone</span>.
          </h1>
          <p className="mt-6 text-[#555] text-base sm:text-lg leading-relaxed max-w-2xl">
            Helencia s'installe en quelques secondes sur iPhone et Android, sans passer par les stores.
            Un geste depuis Safari ou Chrome, et vous retrouvez la plateforme comme une vraie app.
          </p>
        </motion.div>

        {/* Benefits */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Rocket, title: "Accès rapide", desc: "Une icône sur votre écran d'accueil, un tap pour lancer." },
            { icon: Monitor, title: "Plein écran", desc: "Sans barre de navigation ni URL — comme une app native." },
            { icon: Bell, title: "Notifications", desc: "Soyez alerté des nouvelles collabs et messages (optionnel)." },
            { icon: Smartphone, title: "0 Mo installés", desc: "Pas de téléchargement store, mise à jour automatique." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-[#EADDC9] bg-white p-5">
              <div className="w-10 h-10 rounded-full bg-[#FAF3EA] text-[#A26F3E] flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </div>
              <p className="font-serif text-lg text-[#1A1A1A] mt-3">{title}</p>
              <p className="text-sm text-[#6E4B2A] mt-1 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile tab picker */}
      <div className="lg:hidden max-w-[1200px] mx-auto px-6 sm:px-10 mb-6">
        <div className="inline-flex rounded-full bg-[#FAF3EA] p-1 w-full">
          <button
            onClick={() => setTab("ios")}
            className={`flex-1 h-11 rounded-full text-sm font-semibold transition ${tab === "ios" ? "bg-white text-[#1A1A1A] shadow" : "text-[#8B8680]"}`}
            data-testid="tab-ios"
          >
            iPhone (iOS)
          </button>
          <button
            onClick={() => setTab("android")}
            className={`flex-1 h-11 rounded-full text-sm font-semibold transition ${tab === "android" ? "bg-white text-[#1A1A1A] shadow" : "text-[#8B8680]"}`}
            data-testid="tab-android"
          >
            Android
          </button>
        </div>
      </div>

      {/* Guides */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 grid lg:grid-cols-2 gap-6 lg:gap-8">
        <IosGuide hidden={tab !== "ios"} />
        <AndroidGuide hidden={tab !== "android"} />
      </section>

      {/* FAQ */}
      <section className="bg-[#FAF3EA] py-16">
        <div className="max-w-[800px] mx-auto px-6 sm:px-10">
          <p className="caption-label text-[#A26F3E] text-center">Questions fréquentes</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] text-center mt-2">Avant d'installer</h2>
          <div className="mt-10 space-y-3">
            {FAQ_ITEMS.map((f, i) => (
              <details key={i} className="rounded-2xl border border-[#EADDC9] bg-white p-5 group" data-testid={`install-faq-${i}`}>
                <summary className="cursor-pointer font-medium text-[#1A1A1A] flex items-center justify-between">
                  {f.q}
                  <ChevronRight className="w-4 h-4 text-[#8B8680] group-open:rotate-90 transition" />
                </summary>
                <p className="text-sm text-[#6E4B2A] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA bottom */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A]">Prêt à installer ?</h2>
        <p className="text-[#555] mt-3">Rejoignez les talents et créateurs qui utilisent Helencia sur leur téléphone au quotidien.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button onClick={() => navigate("/register")} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12 px-6" data-testid="install-cta-register">
            Créer mon compte Helencia
          </Button>
          <Button onClick={() => navigate("/")} variant="outline" className="rounded-full border-[#1A1A1A] h-12 px-6" data-testid="install-cta-home">
            Retour à l'accueil
          </Button>
        </div>
      </section>
    </div>
  );
}

/* ============================================================ iOS card ============================================================ */
function IosGuide({ hidden }) {
  return (
    <div className={`${hidden ? "hidden" : ""} lg:block rounded-3xl border border-[#EADDC9] bg-white p-6 sm:p-8`} data-testid="install-ios">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-black flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.564 12.65c-.02-2.194 1.788-3.248 1.87-3.3-1.02-1.494-2.608-1.698-3.17-1.72-1.35-.137-2.636.796-3.322.796-.69 0-1.746-.775-2.874-.754-1.478.022-2.84.86-3.6 2.183-1.538 2.667-.393 6.608 1.1 8.768.737 1.057 1.602 2.242 2.737 2.202 1.1-.045 1.517-.713 2.846-.713 1.33 0 1.704.713 2.868.688 1.185-.022 1.934-1.075 2.657-2.137.84-1.228 1.185-2.418 1.206-2.48-.027-.01-2.312-.885-2.34-3.513zM15.46 5.98c.6-.724.998-1.73.89-2.73-.86.034-1.9.572-2.52 1.296-.555.642-1.04 1.666-.912 2.646.96.075 1.942-.49 2.542-1.213z"/></svg>
        </div>
        <div>
          <p className="caption-label text-[#A26F3E]">iPhone & iPad</p>
          <h2 className="font-serif text-2xl text-[#1A1A1A]">Installation iOS (Safari)</h2>
        </div>
      </div>
      <ol className="space-y-5">
        <Step n={1} title="Ouvrez Helencia dans Safari" desc="Important : sur iPhone, l'installation ne marche que depuis Safari (pas Chrome, pas Firefox)." icon={<Smartphone className="w-5 h-5" />} />
        <Step n={2} title="Appuyez sur le bouton Partager" desc="C'est l'icône carrée avec une flèche vers le haut, en bas de l'écran au centre." icon={<Share className="w-5 h-5" />} />
        <Step n={3} title={`Faites défiler puis touchez "Sur l'écran d'accueil"`} desc={"Dans la liste des actions, repérez l'option avec une icône '+' encadrée."} icon={<Plus className="w-5 h-5" />} />
        <Step n={4} title='Confirmez avec "Ajouter"' desc="En haut à droite. L'icône Helencia apparaît alors sur votre écran d'accueil, comme une vraie app." icon={<Check className="w-5 h-5" />} last />
      </ol>
      <div className="mt-6 rounded-2xl bg-[#FAF3EA] p-4 text-xs text-[#6E4B2A] leading-relaxed">
        💡 <strong>Astuce</strong> : lancez Helencia depuis l'icône sur votre home screen — l'app s'ouvre en plein écran, sans la barre d'URL de Safari.
      </div>
    </div>
  );
}

/* ============================================================ Android card ============================================================ */
function AndroidGuide({ hidden }) {
  return (
    <div className={`${hidden ? "hidden" : ""} lg:block rounded-3xl border border-[#EADDC9] bg-white p-6 sm:p-8`} data-testid="install-android">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#3DDC84] flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.78 10.78 0 0 0 1 18h22a10.78 10.78 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/></svg>
        </div>
        <div>
          <p className="caption-label text-[#A26F3E]">Android</p>
          <h2 className="font-serif text-2xl text-[#1A1A1A]">Installation Android (Chrome)</h2>
        </div>
      </div>
      <ol className="space-y-5">
        <Step n={1} title="Ouvrez Helencia dans Chrome" desc="Fonctionne aussi avec Samsung Internet et Edge. Depuis Firefox le flux est similaire." icon={<Smartphone className="w-5 h-5" />} />
        <Step n={2} title='Touchez le menu "⋮" en haut à droite' desc="Les 3 petits points verticaux à côté de la barre d'adresse." icon={<ArrowDown className="w-5 h-5" />} />
        <Step n={3} title={`Choisissez "Installer l'application"`} desc={"Chrome propose aussi parfois une bannière automatique \"Installer Helencia\" en bas d'écran — tapez-la directement."} icon={<Plus className="w-5 h-5" />} />
        <Step n={4} title='Confirmez avec "Installer"' desc="Helencia s'ajoute à vos apps, visible dans le drawer d'applications et sur votre écran d'accueil." icon={<Check className="w-5 h-5" />} last />
      </ol>
      <div className="mt-6 rounded-2xl bg-[#FAF3EA] p-4 text-xs text-[#6E4B2A] leading-relaxed">
        💡 <strong>Astuce</strong> : activez les notifications depuis les réglages de l'app pour être alerté des nouvelles opportunités en temps réel.
      </div>
    </div>
  );
}

function Step({ n, title, desc, icon, last = false }) {
  return (
    <li className="relative pl-14">
      {!last && <span className="absolute left-5 top-10 bottom-[-20px] w-px bg-[#EADDC9]" />}
      <span className="absolute left-0 top-0 w-10 h-10 rounded-full bg-[#1A1A1A] text-[#D4A574] flex items-center justify-center font-serif text-sm">
        {n}
      </span>
      <div className="flex items-start gap-2">
        <div className="text-[#A26F3E] mt-0.5">{icon}</div>
        <div>
          <p className="font-medium text-[#1A1A1A]">{title}</p>
          <p className="text-sm text-[#6E4B2A] mt-1 leading-relaxed">{desc}</p>
        </div>
      </div>
    </li>
  );
}

const FAQ_ITEMS = [
  { q: "Est-ce que c'est une vraie application ?", a: "C'est une Progressive Web App (PWA) — techniquement un site web qui se comporte exactement comme une app native une fois installé. Vous ne verrez aucune différence au quotidien." },
  { q: "Est-ce que c'est gratuit ?", a: "Oui, totalement. L'installation est gratuite et Helencia ne vous facturera jamais pour l'application elle-même." },
  { q: "Est-ce que ça prend de la place sur mon téléphone ?", a: "Non — Helencia n'est pas téléchargée sur votre téléphone comme une app des stores. Elle utilise quelques Mo de cache pour fonctionner hors-ligne, c'est tout." },
  { q: "Est-ce que je peux désinstaller facilement ?", a: "Oui. Sur iPhone, maintenez l'icône puis \"Supprimer l'app\". Sur Android, appuyez longuement et faites glisser vers \"Désinstaller\"." },
  { q: "Les notifications fonctionnent-elles ?", a: "Oui, sur Android directement et sur iOS depuis iOS 16.4. Vous pouvez les activer ou désactiver depuis les réglages de Helencia à tout moment." },
  { q: "Faut-il créer un nouveau compte pour la PWA ?", a: "Non — vous utilisez le même compte Helencia. Si vous êtes déjà connecté sur le web, l'app ouvrira directement votre dashboard." },
];
